import React, { useState, useEffect, useRef } from 'react';
import dummyUser from '../../../../assets/images/users/user-dummy-img.jpg';
import Camera from '../../../../components/common/Camera';
import { swal } from '../../../../helper/swal';

export const PhotoUploader = () => {
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [showWebcam, setShowWebcam] = useState(false);
    const fileInputRef = useRef(null);

    // Common file handler
    const processImageFile = (file, source = 'upload') => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            swal.error('Please provide a valid image file (PNG, JPG, JPEG, WEBP)');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            swal.error('Image size should be less than 10MB');
            return;
        }

        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);

        // Put file in the hidden/actual file input for FormData compatibility
        const input = fileInputRef.current || document.getElementById('avatarInput');
        if (input) {
            try {
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(file);
                input.files = dataTransfer.files;
            } catch (err) {
                console.warn('DataTransfer error:', err);
            }
        }

        if (source === 'paste') {
            swal.success('Photo pasted from clipboard!');
        }
    };

    // Global paste listener (Ctrl + V)
    useEffect(() => {
        const handlePaste = (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;

            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    const file = items[i].getAsFile();
                    if (file) {
                        e.preventDefault();
                        processImageFile(file, 'paste');
                        break;
                    }
                }
            }
        };

        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    }, []);

    // Drag and drop handlers
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer?.files?.[0]) {
            processImageFile(e.dataTransfer.files[0], 'drop');
        }
    };

    const handleRemovePhoto = (e) => {
        e.stopPropagation();
        setPreviewUrl(null);
        const input = fileInputRef.current || document.getElementById('avatarInput');
        if (input) input.value = '';
    };

    return (
        <div className="card create-enquiry-card mb-0 h-100 shadow-sm">
            <div className="card-header py-2 px-3 d-flex align-items-center justify-content-between border-bottom">
                <div className="d-flex align-items-center gap-2">
                    <span className="section-icon-badge bg-soft-primary text-primary">
                        <i className="ri-image-add-line"></i>
                    </span>
                    <h5 className="card-title mb-0 fs-14 fw-semibold">Client / School Photo</h5>
                </div>
                {previewUrl && (
                    <button
                        type="button"
                        onClick={handleRemovePhoto}
                        className="btn btn-sm btn-soft-danger py-0 px-2 fs-11"
                        title="Remove photo"
                    >
                        <i className="ri-delete-bin-line me-1"></i>Remove
                    </button>
                )}
            </div>
            <div className="card-body p-3 d-flex flex-column align-items-center justify-content-between">
                {/* Drag and Drop Zone */}
                <div
                    className={`photo-dropzone w-100 ${isDragging ? 'dragging-active' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    title="Click, drag & drop, or paste (Ctrl+V) an image"
                >
                    <div className="avatar-preview-wrapper">
                        <img
                            src={previewUrl || dummyUser}
                            alt="Profile"
                            className="avatar-preview-img"
                        />
                        <button
                            type="button"
                            className="avatar-badge-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowWebcam(true);
                            }}
                            title="Take photo with Webcam"
                        >
                            <i className="ri-camera-fill"></i>
                        </button>
                    </div>

                    <h6 className="fs-13 fw-semibold text-dark mb-1">
                        {isDragging ? 'Release to upload photo' : 'Drag & Drop or Click to Browse'}
                    </h6>
                    <p className="text-muted fs-11 mb-2">Supports JPG, PNG, WEBP (Max 10MB)</p>

                    <div className="d-flex flex-wrap justify-content-center gap-1">
                        <span className="photo-hint-tag"><i className="ri-clipboard-line"></i> Ctrl + V</span>
                        <span className="photo-hint-tag"><i className="ri-camera-line"></i> Webcam</span>
                        <span className="photo-hint-tag"><i className="ri-drag-drop-line"></i> Drop</span>
                    </div>

                    {/* Hidden input for form submission */}
                    <input
                        ref={fileInputRef}
                        id="avatarInput"
                        type="file"
                        name="avatar"
                        className="d-none"
                        accept="image/png, image/jpeg, image/jpg, image/webp"
                        onChange={(e) => processImageFile(e.target.files?.[0], 'browse')}
                    />
                </div>

                {/* Quick Guidance Box */}
                <div className="enquiry-quick-guide mt-3 w-100 text-start">
                    <div className="enquiry-quick-guide-item">
                        <i className="ri-clipboard-line text-primary mt-1"></i>
                        <span>Paste image directly anywhere using <strong>Ctrl + V</strong>.</span>
                    </div>
                    <div className="enquiry-quick-guide-item">
                        <i className="ri-whatsapp-line text-success mt-1"></i>
                        <span>Check <strong>Same as Phone</strong> to auto-fill WhatsApp.</span>
                    </div>
                    <div className="enquiry-quick-guide-item">
                        <i className="ri-map-pin-line text-danger mt-1"></i>
                        <span>Paste <strong>Google Map</strong> link for navigation.</span>
                    </div>
                </div>

                {/* Webcam Modal overlay */}
                {showWebcam && (
                    <div
                        className="position-relative top-0 bottom-0 d-flex justify-content-center align-items-center start-0 end-0 modal-content border-0 mt-2 p-2 bg-light rounded"
                        style={{ zIndex: 9 }}
                    >
                        <Camera
                            onSave={(file) => {
                                processImageFile(file, 'camera');
                                setShowWebcam(false);
                            }}
                            onClose={() => setShowWebcam(false)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default PhotoUploader;
